// Two distinct surfaces:
//
//   1. `envBuildTarget` — compile-time. `import.meta.env.MODE` is a
//      string literal Vite folds into the bundle, so it's safe to bake
//      this once at module load. The platform abstraction
//      (`lib/platform.ts`) keys off this constant to pick the
//      Tauri-vs-web branch — that's a build-time concern, not a
//      runtime one.
//
//   2. Per-field default-runtime getters
//      (`getDefaultHomeserverUrl`, `getDefaultConsoleUrl`) — runtime.
//      Each reads its own slice
//      from the runtime config (loaded from /config.json by main.tsx)
//      with a fallback to `import.meta.env.VITE_*` for contexts that
//      don't call loadConfig() (tests, storybook, embed). Each field
//      is cached separately so a missing variable only fails the field
//      that needs it. That's the contract ticket #2553 makes explicit:
//      an embed bundle whose host always passes `consoleUrl` as a prop
//      must never trip validation, even if the chat build that produced
//      the bundle didn't set the var.
//
//      Validation runs once-per-field, lazily: a missing value only
//      throws when something actually reads the default for that field.
//      The first read decides the success-or-failure surface for that
//      field forever (or until a test calls
//      `__resetDefaultRuntimeEnvForTests()`).

import { cleanEnv, url } from "envalid";

import { getLoadedConfig } from "~/config";

// `import.meta.env.MODE` is a string literal Vite replaces at build
// time — reliable to read directly without a custom define pass. We
// snapshot it into a local const so TypeScript sees the concrete
// value below.
const MODE = import.meta.env.MODE;

/**
 * Compile-time build target. Encodes which platform-abstraction branch
 * (`tauri` | `web`) the bundle was built for; safe to fold into the
 * embed bundle because every bundle has exactly one target. The `mobile`
 * build mode (Tauri iOS / Android shell) resolves to the `tauri` branch —
 * it runs in a Tauri webview against the native Matrix host.
 */
export const envBuildTarget: "tauri" | "web" =
  MODE === "tauri" || MODE === "mobile" ? "tauri" : "web";

/** Public type alias kept for callers that previously imported it. */
export type BuildTarget = typeof envBuildTarget;

/**
 * Resolved default-runtime-env shape. Mirrors the host-injection
 * surface in `EmbedRuntimeContext` so consumers can swap one for the
 * other without re-shaping fields.
 */
export interface DefaultRuntimeEnv {
  /** Synapse homeserver fronted by MAS. The matrix-rust-sdk discovers
   *  the OIDC authorization endpoint lazily from
   *  `/_matrix/client/v1/auth_metadata` on the first `oauth()` call. */
  homeserverUrl: string;
  /** Nurion Console origin. The Space selector deep-links here. */
  consoleUrl: string;
  /** Hostnames allowed for Matrix meeting join links. */
  meetOrigins: readonly string[];
}

/**
 * Wrap a thunk so the result (or the thrown error) is cached on the
 * first call and replayed on every subsequent call. We store thrown
 * errors so the failure surface is sticky — without this, a test
 * (or production code) that re-runs envalid after a missed-variable
 * throw would see different behaviour on the second call. The
 * `__reset…` helper below exposes a controlled escape hatch for
 * tests.
 */
function memoiseOnceWithError<T>(fn: () => T): {
  read: () => T;
  reset: () => void;
} {
  // Three states:
  //   - `null`: never resolved.
  //   - `T`: resolved successfully, value cached.
  //   - `Error`: previous resolution threw, error cached.
  // We carry the discriminator on the cell shape (kind) instead of
  // `instanceof Error` because a successful resolution could legally
  // produce an `Error` instance some day, and we don't want a
  // happy-path value to look like a sticky failure.
  type Cell<U> = { kind: "value"; value: U } | { kind: "error"; error: Error };
  let cell: Cell<T> | null = null;
  return {
    read: () => {
      if (cell?.kind === "error") throw cell.error;
      if (cell?.kind === "value") return cell.value;
      try {
        const value = fn();
        cell = { kind: "value", value };
        return value;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        cell = { kind: "error", error };
        throw error;
      }
    },
    reset: () => {
      cell = null;
    },
  };
}

const homeserverGate = memoiseOnceWithError(() => {
  const cfg = getLoadedConfig();
  if (cfg?.matrixHomeserverUrl) return cfg.matrixHomeserverUrl;
  return cleanEnv(import.meta.env, {
    VITE_MATRIX_HOMESERVER_URL: url({
      desc: "Synapse homeserver fronted by MAS. The matrix-rust-sdk discovers the OIDC authorization endpoint lazily from /_matrix/client/v1/auth_metadata on the first oauth() call.",
    }),
  }).VITE_MATRIX_HOMESERVER_URL;
});

const consoleGate = memoiseOnceWithError(() => {
  const cfg = getLoadedConfig();
  if (cfg?.consoleUrl) return cfg.consoleUrl;
  return cleanEnv(import.meta.env, {
    VITE_CONSOLE_URL: url({
      desc: "Nurion Console origin. The Space selector deep-links here to create and manage teams.",
    }),
  }).VITE_CONSOLE_URL;
});

const meetOriginsGate = memoiseOnceWithError((): readonly string[] => {
  const cfg = getLoadedConfig();
  if (cfg) return cfg.meetOrigins;
  const raw = import.meta.env.VITE_MEET_ORIGINS;
  if (!raw) return [];
  return raw
    .split(",")
    .map((origin) => origin.trim().toLowerCase())
    .filter((origin) => origin.length > 0);
});

const bookingMeetUrlGate = memoiseOnceWithError((): string => {
  const cfg = getLoadedConfig();
  if (cfg) return cfg.bookingMeetUrl;
  return import.meta.env.VITE_BOOKING_MEET_URL ?? "";
});

const hookshotWidgetUrlGate = memoiseOnceWithError((): string => {
  const cfg = getLoadedConfig();
  if (cfg) return cfg.hookshotWidgetUrl;
  return import.meta.env.VITE_HOOKSHOT_WIDGET_URL ?? "";
});

const hookshotBotUserIdGate = memoiseOnceWithError((): string => {
  const cfg = getLoadedConfig();
  if (cfg) return cfg.hookshotBotUserId;
  return import.meta.env.VITE_HOOKSHOT_BOT_USER_ID ?? "";
});

/**
 * Read the default Synapse homeserver URL. In the standalone shell,
 * this returns the value from /config.json. In test/embed contexts
 * it falls back to the VITE_* env var. Throws (sticky) when both
 * are missing — but only when called. An embed consumer that always
 * supplies `homeserverUrl` as a prop never trips this gate.
 */
export function getDefaultHomeserverUrl(): string {
  return homeserverGate.read();
}

/**
 * Read the default Console origin. See the gate-level comment for
 * why this has no envalid default. Per-field laziness: an embed
 * consumer that overrides `consoleUrl` via props never trips this
 * gate even when its sibling defaults are read.
 */
export function getDefaultConsoleUrl(): string {
  return consoleGate.read();
}

export function getMeetOrigins(): readonly string[] {
  return meetOriginsGate.read();
}

export function getBookingMeetUrl(): string {
  return bookingMeetUrlGate.read();
}

export function getHookshotWidgetUrl(): string {
  return hookshotWidgetUrlGate.read();
}

export function getHookshotBotUserId(): string {
  return hookshotBotUserIdGate.read();
}

/**
 * Aggregator: read every default at once. The standalone shell uses
 * this through `useEmbedRuntime()`'s no-provider fallback. Embed code
 * paths that override one or two fields call the per-field getters
 * individually so a missing-but-overridden var doesn't crash the
 * mount — that's the entire point of the per-field gate split.
 */
export function getDefaultRuntimeEnv(): DefaultRuntimeEnv {
  return {
    homeserverUrl: getDefaultHomeserverUrl(),
    consoleUrl: getDefaultConsoleUrl(),
    meetOrigins: getMeetOrigins(),
  };
}

/**
 * Test hook: reset every cached gate so a test that mutates
 * `import.meta.env` between assertions can re-trigger the envalid
 * pass. Resets the sticky-error cells too — a test that intentionally
 * provoked a throw can call this and try again with a valid env.
 * Production code never calls this.
 */
export function __resetDefaultRuntimeEnvForTests(): void {
  homeserverGate.reset();
  consoleGate.reset();
  meetOriginsGate.reset();
  bookingMeetUrlGate.reset();
  hookshotWidgetUrlGate.reset();
  hookshotBotUserIdGate.reset();
}
